import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { DashboardCard } from '../../components/DashboardCard';
import { apiClient } from '../../utils/apiClient';

const iconUsers = '/figma_asset/Users.svg';
const iconDatabase = '/figma_asset/Database.svg';
const iconInventory = '/figma_asset/Cpu.svg';
const iconSearch = '/figma_asset/Search.svg';
const iconDivider = '/figma_asset/Divider.svg';

const DashboardPage: React.FC = () => {
  const [apiTestResult, setApiTestResult] = useState<string | null>(null);

  const handleApiTest = async () => {
    try {
      const result = await apiClient('/api/auth-test');
      setApiTestResult(JSON.stringify(result, null, 2));
    } catch (error) {
      if (error instanceof Error) {
        setApiTestResult(`エラー: ${error.message}`);
      } else {
        setApiTestResult(`エラー: 不明なエラーが発生しました`);
      }
    }
  };

  return (
    <>
      <div className="flex gap-[60px] items-start w-full mb-[60px]">
        <div className="flex flex-col gap-[10px] items-start">
          <p className="text-[34px] font-medium text-black leading-none">ダッシュボード</p>
        </div>
        <div className="flex flex-col gap-[10px] h-[40px] w-[534px]">
          <div className="flex items-center border border-black rounded-[20px] px-[14px] py-[10px] h-[50px] w-full">
            <img src={iconSearch} alt="search" className="w-[20px] h-[20px] mr-[5px]" />
            <p className="text-[16px] text-black text-center">検索する</p>
          </div>
        </div>
      </div>

      {/* API Test Section */}
      <div className="mb-6 p-4 bg-blue-50 rounded">
        <div className="flex gap-3 items-center">
          <button
            onClick={handleApiTest}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            API認証テスト
          </button>
          <Link
            to="/user-chat"
            className="px-4 py-2 bg-[#ff7a7a] text-white rounded hover:brightness-110"
          >
            ユーザーチャットへ
          </Link>
          <Link
            to="/user/reservation"
            className="px-4 py-2 bg-[#4a6bff] text-white rounded hover:brightness-110"
          >
            予約ページへ
          </Link>
          <Link
            to="/office"
            className="px-4 py-2 bg-[#001973] text-white rounded hover:brightness-110"
          >
            事務画面へ
          </Link>
        </div>
        {apiTestResult && (
          <pre className="mt-4 p-3 bg-white rounded text-sm overflow-auto">{apiTestResult}</pre>
        )}
      </div>

      <div className="flex gap-[30px] w-full mb-[60px]">
        <DashboardCard
          title="ユーザー一覧"
          subtitle="予約・進捗管理"
          icon={iconUsers}
          variant="normal"
          href="/list-user"
        />
        <DashboardCard
          title="企業管理"
          subtitle="企業一覧・登録／解除"
          icon={iconDatabase}
          variant="strong"
          href="/manage-company"
        />
        <DashboardCard
          title="機器在庫管理"
          subtitle="企業一覧・登録／解除"
          icon={iconInventory}
          variant="normal"
          href="/manage-machine"
        />
      </div>
      <div className="flex flex-col gap-[20px] w-full">
        <div className="flex flex-col gap-[20px] w-full">
          <div className="flex gap-[10px] items-start w-full bg-white">
            <div className="flex-1 flex gap-[10px] items-start">
              <p className="text-[16px] font-bold text-black">おしらせ</p>
            </div>
            <div className="flex-1 flex gap-[10px] items-start justify-end self-stretch">
              <p className="text-[14px] text-[#ff7a7a]">＞すべて見る</p>
            </div>
          </div>
          <div className="w-full h-[10px] relative">
            <img
              src={iconDivider}
              alt="divider"
              className="absolute top-[-10%] left-0 w-full h-full"
            />
          </div>
        </div>
        {/* おしらせリスト例 */}
        <div className="flex gap-[20px] w-full">
          <div className="flex-1 border-4 border-[#ff7a7a] rounded-[8px] px-[30px] py-[20px] bg-white">
            <div className="flex gap-[20px] items-start w-full flex-wrap">
              <span className="text-[16px] font-bold text-[#ff7a7a]">NEW</span>
              <span className="text-[16px] text-black whitespace-nowrap">2025.08.20</span>
              <span className="text-[16px] text-black">
                【被験者リスト更新】サカタのタネ株式会社が被験者リストを更新しました。
              </span>
              <span className="text-[16px] text-black">→</span>
            </div>
          </div>
        </div>
        {/* 追加のおしらせ（NEWなし） */}
        <div className="flex flex-col w-full gap-[12px]">
          {[
            '【発送依頼】田中さん（株式会社オープンハウスグループ）にPSG機器を発送してください。メーカーの在庫確認はこちらから',
            '【重要】タスクが更新されています。確認後速やかにタスク完了にしてください。',
            '鈴木太郎さんのパルス検査結果がでました。',
            '【至急】352人のユーザーのパルス検査結果が未確認です。こちらから確認して下さい。',
            '【診断結果更新】50件の診察レポートがアップロードされました。',
            '【至急】未読の診断結果が150件あります',
            '【患者リスト更新】株式会社オープンハウスグループが患者リストをアップロードしました。',
            '【至急】PSG検査の進捗が確認できない患者リストを更新しました。',
            '【新着検査レポート】300件のPSG検査レポートがアップロードされました。',
            '【被験者リスト更新】サカタのタネ株式会社が被験者リストを更新しました。',
          ].map((text, idx) => (
            <div key={idx} className="flex gap-[20px] items-start w-full flex-wrap">
              <span className="text-[16px] text-black whitespace-nowrap">2025.08.20</span>
              <span className="text-[16px] text-black">{text}</span>
              <span className="text-[16px] text-black">→</span>
            </div>
          ))}
        </div>
        {/* 追加のおしらせはここにmapで展開可能 */}
      </div>
    </>
  );
};

export default DashboardPage;
